/**
 * `/support` — send Provide Cars a message, and see every conversation.
 *
 * Top: the new-message form (`?subject=` pre-fills the subject, e.g. "Ask
 * about this request" on a purchase request). Below: the customer's threads,
 * newest activity first, each marked "Sent — awaiting reply", "Reply from
 * Provide Cars" or "Closed". A thread opens at `/support/messages/:id`, where
 * sent and received messages are told apart and the customer can reply.
 *
 * Data source (BFF, own threads only):
 *   - `GET  /api/portal/support/messages`
 *   - `POST /api/portal/support/messages` (body carries a `request_id` so a
 *     retry of the same submission is not stored twice)
 *
 * Spec:
 *   - docs/specs/PORTAL_MIGRATION_FROM_PSM_SSOT.md §4.3 stream G
 *   - docs/specs/PORTAL_INTERNAL_API_CONTRACT.md §2.7 Sp1
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, CardBody, CardHeader, CardTitle, Spinner } from '../components/ui';
import { SupportStatusPill } from '../components/support/SupportStatusPill';
import {
  SUPPORT_BODY_MAX,
  SUPPORT_SUBJECT_MAX,
  createSupportThread,
  formatSupportTime,
  listSupportThreads,
  newRequestId,
  supportErrorCode,
  type SupportThread,
} from '../lib/supportApi';

type ListState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'success'; items: SupportThread[] };

export function SupportPage(): JSX.Element {
  const [list, setList] = useState<ListState>({ kind: 'loading' });
  const [reloadKey, setReloadKey] = useState(0);

  // Form state. `?subject=` pre-fills the subject; the customer can still edit it.
  const [subject, setSubject] = useState(() =>
    typeof window === 'undefined'
      ? ''
      : (new URLSearchParams(window.location.search).get('subject') ?? '').slice(
          0,
          SUPPORT_SUBJECT_MAX,
        ),
  );
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sent, setSent] = useState<SupportThread | null>(null);
  // One request_id per submission, kept across retries of that submission.
  const requestIdRef = useRef<string | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    listSupportThreads(ctrl.signal)
      .then((items) => setList({ kind: 'success', items }))
      .catch(() => {
        if (ctrl.signal.aborted) return;
        setList({ kind: 'error' });
      });
    return () => ctrl.abort();
  }, [reloadKey]);

  const trimmedSubject = subject.trim();
  const trimmedBody = body.trim();
  const subjectInvalid = trimmedSubject.length === 0 || trimmedSubject.length > SUPPORT_SUBJECT_MAX;
  const bodyInvalid = trimmedBody.length === 0 || trimmedBody.length > SUPPORT_BODY_MAX;
  const canSubmit = !subjectInvalid && !bodyInvalid && !sending;

  const handleSubmit = useCallback(
    async (e: React.FormEvent): Promise<void> => {
      e.preventDefault();
      if (!canSubmit) return;
      if (!requestIdRef.current) requestIdRef.current = newRequestId();
      setSending(true);
      setSendError(null);
      try {
        const thread = await createSupportThread({
          subject: trimmedSubject,
          body: trimmedBody,
          requestId: requestIdRef.current,
        });
        setSubject('');
        setBody('');
        requestIdRef.current = null;
        setSent(thread);
        setReloadKey((k) => k + 1);
      } catch (err: unknown) {
        setSendError(supportErrorCode(err));
      } finally {
        setSending(false);
      }
    },
    [canSubmit, trimmedSubject, trimmedBody],
  );

  return (
    <section data-testid="support-page" style={{ display: 'grid', gap: '1.5rem' }}>
      <header>
        <h1 style={{ margin: 0, fontSize: '1.5rem' }}>Support</h1>
        <p style={{ margin: '0.25rem 0 0', color: '#6b7280', fontSize: '0.875rem' }}>
          Send us a message and read our replies here.
        </p>
      </header>

      <Card aria-label="New support message">
        <CardHeader>
          <CardTitle level={2}>Send a new message</CardTitle>
        </CardHeader>
        <CardBody>
          {sent ? (
            <div
              data-testid="support-submitted-banner"
              role="status"
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '0.5rem 1rem',
                background: '#ecfdf5',
                border: '1px solid #a7f3d0',
                borderRadius: '0.375rem',
                padding: '0.75rem 1rem',
                fontSize: '0.875rem',
                color: '#065f46',
              }}
            >
              <span>
                Your message has been sent. Our team will reply within 1–2 business days; the reply
                appears in this conversation.
              </span>
              <span style={{ display: 'inline-flex', gap: '0.75rem' }}>
                {sent.id ? (
                  <Link
                    to={`/support/messages/${sent.id}`}
                    style={{ color: '#065f46', fontWeight: 600 }}
                  >
                    View conversation
                  </Link>
                ) : null}
                <button
                  type="button"
                  onClick={() => setSent(null)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    color: '#065f46',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    textDecoration: 'underline',
                  }}
                >
                  Write a new message
                </button>
              </span>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '0.75rem' }}>
              <label style={fieldLabel}>
                <span style={{ marginBottom: '0.25rem', fontWeight: 500 }}>
                  Subject ({trimmedSubject.length}/{SUPPORT_SUBJECT_MAX})
                </span>
                <input
                  type="text"
                  data-testid="support-subject-input"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  maxLength={SUPPORT_SUBJECT_MAX}
                  placeholder="Summary of your inquiry"
                  style={fieldControl}
                />
              </label>
              <label style={fieldLabel}>
                <span style={{ marginBottom: '0.25rem', fontWeight: 500 }}>
                  Message ({trimmedBody.length}/{SUPPORT_BODY_MAX})
                </span>
                <textarea
                  data-testid="support-body-input"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  maxLength={SUPPORT_BODY_MAX}
                  rows={6}
                  placeholder="Please describe your inquiry in detail"
                  style={{ ...fieldControl, fontFamily: 'inherit', resize: 'vertical' }}
                />
              </label>
              {sendError ? (
                <p
                  data-testid="support-send-error"
                  role="alert"
                  style={{ margin: 0, color: '#991b1b', fontSize: '0.8125rem' }}
                >
                  Your message could not be sent ({sendError}). Please try again in a moment.
                </p>
              ) : null}
              <div>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={!canSubmit}
                  data-testid="support-submit-button"
                >
                  {sending ? 'Sending…' : 'Send'}
                </Button>
              </div>
            </form>
          )}
        </CardBody>
      </Card>

      <Card aria-label="Your messages">
        <CardHeader>
          <CardTitle level={2}>Your messages</CardTitle>
        </CardHeader>
        <CardBody>
          {list.kind === 'loading' ? (
            <div aria-busy="true" data-testid="support-threads-loading">
              <Spinner block label="Loading your messages" />
            </div>
          ) : null}
          {list.kind === 'error' ? (
            <p
              data-testid="support-threads-error"
              style={{ margin: 0, fontSize: '0.875rem', color: '#991b1b' }}
            >
              Your messages could not be loaded right now. Please reload the page in a moment.
            </p>
          ) : null}
          {list.kind === 'success' && list.items.length === 0 ? (
            <p
              data-testid="support-threads-empty"
              style={{ margin: 0, fontSize: '0.875rem', color: '#4b5563' }}
            >
              No messages yet. Messages you send and our replies will appear here.
            </p>
          ) : null}
          {list.kind === 'success' && list.items.length > 0 ? (
            <ul
              data-testid="support-threads"
              style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: '0.5rem' }}
            >
              {list.items.map((t) => (
                <li key={t.id}>
                  <Link
                    to={`/support/messages/${t.id}`}
                    data-testid={`support-thread-${t.id}`}
                    style={{
                      display: 'grid',
                      gap: '0.25rem',
                      padding: '0.75rem 0.875rem',
                      border: `1px solid ${t.status !== 'closed' && t.last_author === 'support' ? '#93c5fd' : '#e5e7eb'}`,
                      borderRadius: '0.5rem',
                      textDecoration: 'none',
                      color: 'inherit',
                      background: '#fff',
                    }}
                  >
                    <span
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '0.25rem 0.75rem',
                      }}
                    >
                      <strong style={{ fontSize: '0.9375rem', color: '#111827' }}>
                        {t.subject}
                      </strong>
                      <SupportStatusPill thread={t} />
                    </span>
                    <span style={{ fontSize: '0.8125rem', color: '#4b5563' }}>
                      {t.reply_count + 1} {t.reply_count === 0 ? 'message' : 'messages'} · Last
                      activity {formatSupportTime(t.last_activity_at)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          ) : null}
        </CardBody>
      </Card>
    </section>
  );
}

const fieldLabel: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  fontSize: '0.75rem',
  color: '#374151',
};

const fieldControl: React.CSSProperties = {
  padding: '0.5rem 0.625rem',
  border: '1px solid #d1d5db',
  borderRadius: '0.25rem',
  fontSize: '0.875rem',
};
